import { useEffect, useRef, useState } from 'react';
import { FormApi } from 'informed';
import Router from 'next/router';
import { isAiConfigAvailableForUser } from 'shared-helpers/aiTools';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { CertificatesSelectTemplate } from 'components/admin/settings/certificates';
import { Button } from 'components/common';
import { ProductCustomAttributes } from 'components/common/customAttributes';
import {
  Checkbox,
  Form,
  FormGroup,
  Text,
  TextArea
} from 'components/common/form';
import { SelectInput } from 'components/common/form/common';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';
import { CoursesCreateFromFile, CoursesCreateMoodle } from 'components/courses';

import type { CourseFileUploader } from './CoursesCreateFromFile';

type CreateMode = 'default' | 'file' | 'json' | 'moodle';

const CoursesCreate = () => {
  const { dispatch } = useDispatch();
  const { messages, serverConfig, user } = useReduxState();

  const buttonElement = useRef<HTMLButtonElement>(undefined);
  const formApi = useRef<FormApi>(undefined);
  const jsonInputContainer = useRef<HTMLDivElement>(undefined);
  const titleInputContainer = useRef<HTMLDivElement>(undefined);
  const uploader = useRef<CourseFileUploader>(undefined);

  const [hasFile, setHasFile] = useState(false);
  const [isUploadRunning, setIsUploadRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<CreateMode>('default');
  const [modalVisible, setModalVisible] = useState(false);

  const isJsonImportAvailable = Boolean(
    user &&
      (serverConfig.courseEnableJsonImportForAdmins ||
        user.roles.includes('superadmin'))
  );

  const isFileGenerationAvailable = Boolean(
    serverConfig.featureFlags?.enableAiCourseGeneration &&
      serverConfig.aiConfig?.modelsProvider === 'vertex' &&
      isAiConfigAvailableForUser({ serverConfig, user })
  );

  const isMoodleImportAvailable = Boolean(
    user?.roles.includes('vuoadmin') &&
      serverConfig.featureFlags?.enableMoodleImporter
  );

  const modeOptions = [
    {
      helpText: messages.coursesNewCourseInfo,
      label: messages.coursesNewCourse,
      value: 'default'
    },
    ...(isJsonImportAvailable
      ? [
          {
            helpText: messages.coursesImportJsonInfo,
            label: messages.coursesImportJson,
            value: 'json'
          }
        ]
      : []),
    ...(isFileGenerationAvailable
      ? [
          {
            helpText: serverConfig.globalDisableBackgroundJobs
              ? messages.coursesCreateFromFileBackgroundJobsOff
              : messages.coursesCreateFromFileInfo,
            isDisabled: Boolean(serverConfig.globalDisableBackgroundJobs),
            label: messages.coursesCreateFromFile,
            value: 'file'
          }
        ]
      : []),
    ...(isMoodleImportAvailable
      ? [
          {
            helpText: serverConfig.globalDisableBackgroundJobs
              ? messages.coursesImportMoodleBackgroundJobsOff
              : messages.coursesImportMoodleInfo,
            isDisabled: Boolean(serverConfig.globalDisableBackgroundJobs),
            label: messages.coursesImportMoodle,
            value: 'moodle'
          }
        ]
      : [])
  ];

  useEffect(() => {
    if (modalVisible) {
      titleInputContainer.current?.querySelector('input').focus();
    }
  }, [modalVisible]);

  useEffect(() => {
    if (mode === 'json') {
      jsonInputContainer.current?.querySelector('textarea').focus();
    } else {
      titleInputContainer.current?.querySelector('input').focus();
    }
  }, [mode]);

  const handleJsonPaste = (ev) => {
    const textareaEl = ev.target;

    setTimeout(() => {
      try {
        const jsonData = JSON.parse(textareaEl.value.replace(/^[^{]*{/, '{'));
        formApi.current?.setValue('title', jsonData?.data?.title || '');
        return undefined;
      } catch (err) {
        console.log(err);
        return 'Invalid JSON';
      }
    });
  };

  const changeMode = (newMode: CreateMode) => {
    if (newMode === mode) return;

    // the file and Moodle modes mount a new, empty uploader each time
    setHasFile(false);
    setMode(newMode);
  };

  const hideModal = () => {
    setHasFile(false);
    setIsUploadRunning(false);
    setLoading(false);
    setMode('default');
    setModalVisible(false);
    formApi.current?.reset();
    setTimeout(() => buttonElement.current?.focus());
  };

  const showModal = () => {
    setModalVisible(true);
  };

  const startUpload = () => {
    const {
      CertificateTemplateId,
      customAttributes,
      language,
      title,
      useOwnKnowledge
    } = formApi.current.getState().values;
    // Sent as form fields next to the file, so every value is a string. Empty
    // values are sent too: setMeta merges, so a skipped key would keep the
    // value from an earlier attempt.
    uploader.current?.uppy.setMeta({
      CertificateTemplateId: CertificateTemplateId || '',
      customAttributes: customAttributes
        ? JSON.stringify(customAttributes)
        : '',
      title,
      ...(mode === 'file' && {
        language,
        useOwnKnowledge: Boolean(useOwnKnowledge)
      })
    });
    setIsUploadRunning(true);
    uploader.current?.handleUpload();
  };

  const submit = async (values) => {
    // the file and Moodle modes submit through their own button, not the form
    if (mode === 'file' || mode === 'moodle') return;

    try {
      const titleError = await validateTitle(values.title);
      if (titleError) {
        formApi.current?.setError('title', titleError);
        return;
      }

      setLoading(true);

      const data = {
        title: values.title,
        ...(mode === 'json'
          ? {
              captureNewBlockImages: values.captureNewBlockImages || false,
              courseData: JSON.parse(values.json.replace(/^[^{]*{/, '{'))
            }
          : {
              customAttributes: values.customAttributes
            }),
        ...(values.CertificateTemplateId && {
          CertificateTemplateId: values.CertificateTemplateId
        })
      };

      const response = await api.post('/admin/courses', data);
      dispatch.addNotice({
        text: messages.coursesCreateCourseSuccess,
        type: 'success'
      });
      let courseUrl = '/admin/courses/[courseSlug]';
      let courseAs = `/admin/courses/${response.data.course.slug}`;
      if (mode !== 'json') {
        courseUrl += '/edit?newChapter=true';
        courseAs += '/edit?newChapter=true';
      }
      Router.push(courseUrl, courseAs);
    } catch (err) {
      setLoading(false);
      await dispatch.handleErrorDispatch(err);
    }
  };

  /*
   * Validate JSON input with JSON parser.
   * Replace any characters before first curly brace, e.g. U+FEFF in UTF-16 files
   */
  const validateJson = (value) => {
    try {
      JSON.parse(value.replace(/^[^{]*{/, '{'));
      return undefined;
    } catch (err) {
      console.log(err);
      return messages.courseLanguageVersionsInvalidJson;
    }
  };

  const validateTitle = async (value) => {
    if (!value) return messages.coursesTitleEmpty;

    try {
      await api.head(`/admin/courses?title=${value}`);
      return messages.coursesTitleInUse;
    } catch {
      return undefined;
    }
  };

  return (
    <>
      <Button
        category="success"
        icon="plus"
        onClick={showModal}
        ref={buttonElement}
        text={messages.coursesCreateCourse}
      />
      <ModalPortal>
        <Form
          getApi={(form) => (formApi.current = form)}
          initialValues={{ CertificateTemplateId: 0 }}
          onSubmit={submit}
        >
          {({ formState }) => (
            <Modal
              onHide={isUploadRunning ? () => null : hideModal}
              title={messages.coursesCreateCourse}
              visible={modalVisible}
            >
              <ModalContent>
                {modeOptions.length > 1 ? (
                  <FormGroup
                    htmlFor="create-mode-input"
                    label={messages.coursesCreateMode}
                  >
                    <SelectInput
                      id="create-mode"
                      inputId="create-mode-input"
                      isDisabled={isUploadRunning}
                      onChange={(option: (typeof modeOptions)[0]) =>
                        changeMode(option.value as CreateMode)
                      }
                      options={modeOptions}
                      value={modeOptions.find(
                        (option) => option.value === mode
                      )}
                    />
                  </FormGroup>
                ) : null}

                <FormGroup htmlFor="title" label={messages.coursesCourseTitle}>
                  <Text
                    customValidateAsync={validateTitle}
                    field="title"
                    id="title"
                    inputContainerRef={(el) =>
                      (titleInputContainer.current = el)
                    }
                  />
                </FormGroup>

                {serverConfig.certificatesEnableSelection &&
                  serverConfig.certificatesEnableSelectionOnCreate && (
                    <CertificatesSelectTemplate
                      disabled={isUploadRunning}
                      type="course"
                    />
                  )}

                {mode === 'file' ? (
                  <CoursesCreateFromFile
                    isUploadRunning={isUploadRunning}
                    onClose={hideModal}
                    onHasFileChange={setHasFile}
                    onUploadRunningChange={setIsUploadRunning}
                    uploaderRef={uploader}
                  />
                ) : null}

                {mode === 'moodle' ? (
                  <CoursesCreateMoodle
                    onClose={hideModal}
                    onHasFileChange={setHasFile}
                    onUploadRunningChange={setIsUploadRunning}
                    uploaderRef={uploader}
                  />
                ) : null}

                {mode === 'json' ? (
                  <>
                    <FormGroup htmlFor="json" label="JSON">
                      <TextArea
                        error={formState.errors.json}
                        field="json"
                        id="json"
                        rows={8}
                        textareaContainerRef={(el) =>
                          (jsonInputContainer.current = el)
                        }
                        onPaste={handleJsonPaste}
                        validate={validateJson}
                        validateOnChange={true}
                      />
                    </FormGroup>
                    {user?.roles.includes('vuoadmin') ? (
                      <FormGroup>
                        <Checkbox
                          field="captureNewBlockImages"
                          id="capture-new-block-images"
                          label={'Capture new block images'}
                        />
                      </FormGroup>
                    ) : null}
                  </>
                ) : null}

                {mode !== 'json' &&
                serverConfig.courseCustomAttributes?.some(
                  (attr) => attr.required || attr.displayInCreateForm
                ) ? (
                  <div style={{ marginTop: 30 }}>
                    <ProductCustomAttributes
                      hideWarnings={true}
                      renderOnlyRequiredOrDisplayable={true}
                      type="course"
                    />
                  </div>
                ) : null}
              </ModalContent>
              <ModalFooter>
                <Button
                  category="light"
                  disabled={isUploadRunning}
                  onClick={hideModal}
                  text={messages.commonCancel}
                />

                {mode === 'default' ? (
                  <Button
                    category="success"
                    disabled={formState.invalid || formState.pristine}
                    loading={loading}
                    text={messages.commonCreate}
                    type="submit"
                  />
                ) : null}

                {mode === 'json' ? (
                  <Button
                    category="success"
                    disabled={!formState.values.json || formState.errors.json}
                    loading={loading}
                    text={messages.commonImport}
                    type="submit"
                  />
                ) : null}

                {mode === 'file' || mode === 'moodle' ? (
                  <Button
                    category="success"
                    disabled={
                      !hasFile || !formState.values.title || formState.invalid
                    }
                    loading={isUploadRunning}
                    onClick={startUpload}
                    text={
                      mode === 'moodle'
                        ? messages.commonImport
                        : messages.commonCreate
                    }
                  />
                ) : null}
              </ModalFooter>
            </Modal>
          )}
        </Form>
      </ModalPortal>
    </>
  );
};

export default CoursesCreate;
